import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { loggedGet } from './loggedFetch';

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

// Phase F: sub-brand chip row. Renders one chip per product_family that has
// spend under the current vendor (e.g. Cisco shows Meraki / Webex / Splunk /
// AppDynamics chips; HPE shows Aruba / Juniper; PAN shows CyberArk).
// Vendors without rolled-up sub-brands render nothing.
//
// Selecting a chip threads ?product_family=<family> through every VCA
// endpoint that supports the filter (snapshot, trajectory, heatmap,
// competitors, displacement, agencies, transactions). Renewals and Partners
// are vendor-scoped today (their underlying matviews don't carry
// component_id) — they receive the param for forward-compat but ignore it.
function ProductFamilyChips({ vendor, value, onChange, strict, includeLowConfidence }) {
  const qs = new URLSearchParams();
  if (!strict) qs.set('strict', 'false');
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  const suffix = qs.toString() ? `?${qs}` : '';
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/product-families${suffix}`;

  const { data } = useQuery({
    queryKey: ['vendor-analysis', 'product-families', vendor, strict, includeLowConfidence],
    queryFn: loggedGet('ProductFamilyChips:fetch', path),
    enabled: !!vendor,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const families = data?.families || [];
  if (!families.length) return null;

  return (
    <div className="vca-pf-chips" role="tablist" aria-label="Product family filter">
      <span className="vca-pf-chips__label">
        <Layers size={12} /> Product line
      </span>
      <button
        type="button"
        role="tab"
        aria-selected={!value}
        className={`vca-pf-chip ${!value ? 'vca-pf-chip--active' : ''}`}
        onClick={() => onChange(null)}
      >
        All
      </button>
      {families.map((f) => (
        <button
          key={f.product_family}
          type="button"
          role="tab"
          aria-selected={value === f.product_family}
          className={`vca-pf-chip ${value === f.product_family ? 'vca-pf-chip--active' : ''}`}
          onClick={() => onChange(f.product_family)}
          title={`${f.po_line_count} POs · ${f.agency_count} agencies`}
        >
          {f.product_family}
          <span className="vca-pf-chip__amt">{formatCurrency(f.total_spend)}</span>
        </button>
      ))}
    </div>
  );
}

export default ProductFamilyChips;
